import React, { useEffect, useState } from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { RosterCell } from '../../interfaces/shift/RosterCell';
import { ShiftRosterService } from '../../services/shift-roster.service';
import { useToast } from '../../context/ToastContext';

import SearchableSelect from '../common/SearchableSelect';
type StatusColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusChipColor = (status: string): StatusColor => {
    switch (status) {
        case 'SCHEDULED':  return 'default';
        case 'WORKED':     return 'success';
        case 'ABSENT':     return 'error';
        case 'ON_LEAVE':   return 'warning';
        case 'HOLIDAY':    return 'info';
        case 'CANCELLED':  return 'secondary';
        default:           return 'default';
    }
};

const KNOWN_ERRORS: Record<string, string> = {
    CROSS_INSTITUTION_REFERENCE: 'The selected shift belongs to a different institution.',
    'ShiftInstance not found': 'This roster instance no longer exists — please refresh.',
};

const parseBackendError = (err: unknown): string => {
    const axErr = err as any;
    const msg: string = axErr?.response?.data?.message || axErr?.response?.data || axErr?.message || '';
    for (const [key, label] of Object.entries(KNOWN_ERRORS)) {
        if (msg.includes(key)) return label;
    }
    return msg || 'An unexpected error occurred.';
};

interface Props {
    open: boolean;
    onClose: () => void;
    cell: RosterCell;
    institutionId: string;
    shifts: Shift[];
    onSaved: () => void;
}

const RosterCellOverrideDialog: React.FC<Props> = ({ open, onClose, cell, institutionId, shifts, onSaved }) => {
    const toast = useToast();

    const [selectedShiftId, setSelectedShiftId] = useState<string>(cell.shiftId ?? '');
    const [notes, setNotes]     = useState('');
    const [saving, setSaving]   = useState(false);
    const [shiftError, setShiftError] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedShiftId(cell.shiftId ?? '');
            setNotes('');
            setSaving(false);
            setShiftError('');
        }
    }, [open, cell]);

    const activeShifts = shifts.filter(s => s.isActive);

    const handleSave = async () => {
        if (!selectedShiftId) {
            setShiftError('Please select a shift');
            return;
        }
        setSaving(true);
        try {
            await ShiftRosterService.overrideCell(institutionId, cell.instanceId, {
                newShiftId: selectedShiftId,
                notes: notes.trim() || undefined,
            });
            toast.showSuccess('Override saved');
            onSaved();
        } catch (err) {
            toast.showApiError(err, parseBackendError(err));
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm('Reset this cell to its original auto-generated value?')) return;
        setSaving(true);
        try {
            await ShiftRosterService.resetCell(institutionId, cell.instanceId);
            toast.showSuccess('Override reset');
            onSaved();
        } catch (err) {
            toast.showApiError(err, parseBackendError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.override.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>
                Override Roster Cell
            </DialogTitle>
            <DialogContent>
                <Stack data-help-section="shifts.override.section.details" spacing={2} sx={{ pt: 1 }}>
                    {/* Summary */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                            {cell.businessDate}
                        </Typography>
                        <Chip
                            label={cell.status}
                            color={statusChipColor(cell.status)}
                            size="small"
                        />
                        {cell.isManualOverride && (
                            <Chip label="★ Manual Override" size="small" color="warning" />
                        )}
                    </Stack>

                    {/* Shift select */}
                    <SearchableSelect
                        options={activeShifts}
                        value={selectedShiftId}
                        onChange={id => { setSelectedShiftId(id); setShiftError(''); }}
                        getOptionLabel={s => `${s.name} (${s.code})`}
                        data-help="shifts.override.field.shift" label="Shift"
                        required
                        error={!!shiftError}
                        helperText={shiftError || undefined}
                        data-testid="override-shift-select"
                    />

                    {/* Notes */}
                    <TextField
                        data-help="shifts.override.field.notes" label="Notes"
                        multiline
                        rows={3}
                        fullWidth
                        value={notes}
                        onChange={e => setNotes(e.target.value.slice(0, 500))}
                        helperText={`${notes.length}/500`}
                        inputProps={{ 'data-testid': 'override-notes', maxLength: 500 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                {cell.isManualOverride && (
                    <Button
                        data-help="shifts.override.reset" onClick={handleReset}
                        color="warning"
                        disabled={saving}
                        data-testid="override-reset-btn"
                    >
                        Reset
                    </Button>
                )}
                <Button data-help="shifts.override.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    data-help="shifts.override.save" data-testid="override-save-btn"
                >
                    {saving ? 'Saving…' : 'Save Override'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RosterCellOverrideDialog;
